// Libraries
import { all, call, put, select, takeLatest } from 'redux-saga/effects';

// Dependencies
import { getMyManagerProfile } from 'features/manager';
import {
  getUserPreferences,
  updateHighContrastMode,
} from 'features/userPreferences';
import serviceAuth, { TROUBLESHOOT_TOKEN } from 'services/auth';
import { withHandledStatuses } from 'services/api/toastConfig';
import serviceUser from 'services/user/user';
import { ACCESS_TOKEN, decodeJwtPayload, getAccessToken } from 'services/utils/accessToken';
import { actions as memberActions } from 'store/modules/member';
import { actions as mentorActions } from 'store/modules/mentor';
import { actions as translationActions } from 'store/modules/translations';
import normalizeUser from 'store/normalizers/userNormalizer';
import { loginMixpanel, signupMixpanel } from 'utils/mixpanel';

// Local Dependencies
import queryString from 'utils/queryString';
import userActions from './actions';
import userSelectors from './selectors';
import types from './types';

// /account answers 400 for an account without an Entrepreneur or Mentor profile
const ACCOUNT_WITHOUT_PROFILE_STATUS = 400;

function* settle(effect) {
  try {
    return { data: yield effect };
  } catch (error) {
    return { error };
  }
}

// Legacy admins still need their Mentor data from /account, while manager-only accounts have only
// the v2 manager profile, so managers load both.
function* fetchManagerAccount() {
  const [account, profile] = yield all([
    call(
      settle,
      call(serviceUser.getAccount, withHandledStatuses([ACCOUNT_WITHOUT_PROFILE_STATUS]))
    ),
    call(settle, call(getMyManagerProfile)),
  ]);
  const isManagerOnly =
    account.error?.response?.status === ACCOUNT_WITHOUT_PROFILE_STATUS && Boolean(profile.data);
  if (account.error && !isManagerOnly) throw account.error;

  return { response: account.data ?? null, managerProfile: profile.data ?? null };
}

export function* fetchUser(action = { payload: {} }) {
  const { callback } = action.payload;
  try {
    const claims = decodeJwtPayload(getAccessToken() || '') || {};
    let response;
    let managerProfile = null;
    if (claims.is_manager) {
      ({ response, managerProfile } = yield call(fetchManagerAccount));
    } else {
      response = yield call(serviceUser.getAccount);
    }
    // Manager-only accounts have no UserPreferences yet (the endpoint answers 404 for them)
    if (response) {
      try {
        const preferences = yield call(getUserPreferences);
        yield put(
          userActions.userPreferencesFetchSucceeded({
            preferences,
          })
        );
      } catch (e) {
        yield put(
          userActions.userPreferencesFetchFailed({
            message: (e.response && e.response.data) || e.message,
          })
        );
      }
    }
    const { user, member, mentor } = response
      ? yield call(normalizeUser, response.data)
      : {
          user: {
            uuid: claims.sub,
            username: managerProfile.email,
            email_verified: claims.email_verified,
          },
        };
    if (typeof callback === 'function') callback({ user, member, mentor });
    yield put(
      userActions.userFetchSucceeded({
        userData: {
          email_verified: user.email_verified,
          ...user,
          manager: managerProfile && { ...managerProfile },
        },
      })
    );
    yield member && put(memberActions.memberFetchSucceeded({ memberData: member }));
    yield mentor && put(mentorActions.mentorFetchSucceeded({ mentorData: mentor }));

    const profile = member || mentor || managerProfile;
    if (profile) {
      yield put(translationActions.translationsChange({ selectedLanguage: profile.language }));
    }
    const userType = yield select(userSelectors.selectActorAnalyticsType);
    loginMixpanel(member, mentor, userType, managerProfile);
  } catch (e) {
    yield put(userActions.userFetchFailed({ message: e.message }));
  }
}

export function* userPreferencesEdit(action) {
  try {
    const preferences = yield call(updateHighContrastMode, action.payload.highContrastMode);
    yield put(
      userActions.userPreferencesEditSucceeded({
        preferences,
      })
    );
    if (action.callback) {
      yield call(action.callback);
    }
  } catch (e) {
    if (action.callback) {
      yield call(action.callback, e.response);
    }
    yield put(
      userActions.userPreferencesEditFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

export function* userRegister(action) {
  const { username, password, re_password: rePassword, member, mentor, organization, program, callback } =
    action.payload;
  try {
    const user = yield call(serviceAuth.registerAuth, {
      username,
      password,
      re_password: rePassword,
      member,
      mentor,
      organization,
      program,
    });
    yield put(userActions.userRegisterSucceeded({ userData: user.data }));

    if (callback) {
      yield call(callback, undefined, user.data);
    }
    // Mixpanel event
    signupMixpanel(user.data);
  } catch (e) {
    yield put(userActions.userRegisterFailed({ message: e.response.data }));

    if (callback) {
      // calling callback with error data
      yield call(callback, e.response?.data);
    }
  }
}

// Check if the troubleshoot token is present in the URL and if we're in the troubleshoot mode on a iframe or not
const queryParams = new URLSearchParams(window.location.search);
const troubleshoot = queryParams.get('troubleshoot');

// Get the token from the local storage or the session storage according to the troubleshoot mode
const usedToken = troubleshoot
  ? sessionStorage.getItem(TROUBLESHOOT_TOKEN)
  : localStorage.getItem(ACCESS_TOKEN);

export function* userRestore() {
  const params = queryString.parse(document.location.search);
  const accessToken = usedToken || params.access_token;

  if (accessToken) {
    yield fetchUser();
    yield put(userActions.userIsAccessible());
  }
  yield put(userActions.userIsAccessible());
}

export function* forgotPassword(action) {
  const { email } = action.payload;
  try {
    yield call(serviceAuth.forgotPassword, email);
    yield put(userActions.forgotPasswordSucceeded());
  } catch (e) {
    yield put(
      userActions.forgotPasswordFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

export function* resetPassword(action) {
  try {
    const { user, password } = action.payload;
    yield call(serviceAuth.resetPassword, action.payload);
    yield put(userActions.userLogin({ username: user, password }));
    if (action.callback) {
      yield call(action.callback);
    }
  } catch (e) {
    yield put(
      userActions.userEditFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

export function* setPassword(action) {
  try {
    const { user, re_new_password: reNewPassword, new_password: newPassword, current_password: currentPassword } = action.payload;
    yield call(serviceAuth.setNewPassword, {
      user,
      re_new_password: reNewPassword,
      new_password: newPassword,
      current_password: currentPassword,
    });
    yield put(userActions.userLogin({ username: user, password: newPassword }));
    if (action.callback) {
      yield call(action.callback);
    }
  } catch (e) {
    yield put(
      userActions.userEditFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

export function* userEdit(action) {
  const { uid, token, user, password } = action.payload;
  try {
    yield call(serviceAuth.resetPassword, {
      uid,
      token,
      user,
      password,
    });
    yield put(userActions.userLogin({ username: user, password }));
  } catch (e) {
    yield put(
      userActions.forgotPasswordFailed({
        message: (e.response && e.response.data) || e.message,
      })
    );
  }
}

function* authSaga() {
  yield all([
    takeLatest(types.USER_RESTORE, userRestore),
    takeLatest(types.USER_REGISTER, userRegister),
    takeLatest(types.USER_FORGOT_PASSWORD, forgotPassword),
    takeLatest(types.USER_RESET_PASSWORD, resetPassword),
    takeLatest(types.USER_SET_PASSWORD, setPassword),
    takeLatest(types.USER_EDIT, userEdit),
    takeLatest(types.USER_FETCH_REQUESTED, fetchUser),
    takeLatest(types.USER_PREFERENCES_EDIT, userPreferencesEdit),
  ]);
}

export default authSaga;
